'use client'

import { useCallback, useEffect, useRef } from 'react'
import { Briefcase } from 'lucide-react'
import { JobCard } from './job-card'
import type { JobCardData } from '@/types/api.types'

interface JobBoardGridProps {
  jobs: JobCardData[]
  hasMore: boolean
  isLoading: boolean
  onLoadMore: () => void
  isAuthenticated?: boolean
}

export function JobBoardGrid({ jobs, hasMore, isLoading, onLoadMore, isAuthenticated = true }: JobBoardGridProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Disconnect observer on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
    }
  }, [])

  const lastJobRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return
      if (observerRef.current) observerRef.current.disconnect()

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting && hasMore) {
          onLoadMore()
        }
      })

      if (node) observerRef.current.observe(node)
    },
    [isLoading, hasMore, onLoadMore],
  )

  if (jobs.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-bg-elevated)]">
          <Briefcase className="h-6 w-6 text-[var(--color-text-muted)]" />
        </div>
        <h3
          className="mt-4 text-base font-semibold text-[var(--color-text-primary)]"
          style={font}
        >
          No jobs found
        </h3>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]" style={font}>
          Try adjusting your filters or check back later for new opportunities.
        </p>
      </div>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {jobs.map((job, index) => (
        <div
          key={job.id}
          ref={index === jobs.length - 1 ? lastJobRef : undefined}
        >
          <JobCard job={job} isAuthenticated={isAuthenticated} />
        </div>
      ))}

      {isLoading && (
        <>
          {[1, 2, 3].map((i) => (
            <div
              key={`skeleton-${i}`}
              className="h-48 animate-pulse rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--color-bg-elevated)]"
            />
          ))}
        </>
      )}
    </div>
  )
}
